import React, { useState } from 'react';

import WidgetSettingsShowDataForEditor from 'widgets/WidgetSettingsEditing/WidgetSettingsShowDataForEditor';
import WidgetNewportAppSettingsEditor from 'widgets/WidgetSettingsEditing/WidgetNewportAppSettingsEditor';
import { useWidgetConfigUpdater } from 'widgets/WidgetSettingsEditing/useWidgetConfigUpdater';

import { CapsuleSettingsSymbolEditor } from 'widgets/tiles/components/tile/CapsuleSettingsSymbolEditor';
import { PerfVsIndexCapsuleConfigState } from 'widgets/perfVsIndexCapsule/store/config/PerfVsIndexCapsuleConfigState';
import { usePerfVsIndexCapsuleConfigSlice } from 'widgets/perfVsIndexCapsule/store/config/configslice';
import './PerfVsIndexCapsuleSettings.scss';
import { useIndexMappingsDisplayText } from 'symbols/hooks/useIndexMappingsDisplayText';
import {
    useFetchIndexMappingsUserPreference,
    useSelectIndexMappingsUserPreference,
} from 'symbols/indexMappings/hooks';
import { IndexMappingsEditorModal } from 'symbols/indexMappings/IndexMappingsEditorModal/IndexMappingsEditorModal';
import { RadioGroup } from '@instinet-analytics/semantic-ui-components';
import WidgetSettingsCheckboxValueEditor from '../../../WidgetSettingsEditing/WidgetSettingsCheckboxValueEditor';

export const PerfVsIndexCapsuleSettingsText = `Perf Vs Index Capsule Settings`;

const DefaultOptionValue = 'default';
const CustomOptionValue = 'custom';

const selectedIndexOptions = [
    { key: DefaultOptionValue, label: 'Default (Newport Mappings)' },
    { key: CustomOptionValue, label: 'Custom' },
];

const TitleSymbolOptionValue = 'sym';
const TitleDescOptionValue = 'desc';

const titleOptions = [
    { key: TitleSymbolOptionValue, label: 'Show Symbol' },
    { key: TitleDescOptionValue, label: 'Show Description' },
];

export interface PerfVsIndexSettingsProps {
    config?: PerfVsIndexCapsuleConfigState;
    onChange?: (nextDelta: Partial<PerfVsIndexCapsuleConfigState>) => void;
}

export default function PerfVsIndexCapsuleSettings(
    props: PerfVsIndexSettingsProps
) {
    const { onChange, config: propsConfig } = props;
    const { config: configSliceConfig, dispatchConfigChanged } =
        usePerfVsIndexCapsuleConfigSlice();
    const {
        debouncedApplyWidgetConfigChange,
        immediateApplyWidgetConfigChange,
    } = useWidgetConfigUpdater(dispatchConfigChanged, onChange);
    const config = propsConfig ?? configSliceConfig;

    const userPrefMappings = useSelectIndexMappingsUserPreference();
    useFetchIndexMappingsUserPreference();

    const userPrefsDisplay = useIndexMappingsDisplayText();

    const { indexFallback, symbolInTitle } = config;

    const isDefault = indexFallback === 'DEFAULT';
    const optionValue = isDefault ? DefaultOptionValue : CustomOptionValue;
    const titleOptionValue = symbolInTitle
        ? TitleSymbolOptionValue
        : TitleDescOptionValue;

    const [selectedIndexOption, setSelectedIndexOption] = useState(optionValue);
    const [selectedTitleOption, setSelectedTitleOption] =
        useState(titleOptionValue);

    const onChangeIndex = (nextValue: string) => {
        const nextIndexFallback =
            nextValue === DefaultOptionValue ? 'DEFAULT' : '';
        setSelectedIndexOption(nextValue);
        debouncedApplyWidgetConfigChange({ indexFallback: nextIndexFallback });
    };

    const onChangeTitle = (option: string) => {
        const next = option === TitleSymbolOptionValue;
        setSelectedTitleOption(option);
        debouncedApplyWidgetConfigChange({ symbolInTitle: next });
    };

    return (
        <div className="PerfVsIndexCapsuleSettings">
            <div className="group ">
                <CapsuleSettingsSymbolEditor
                    config={config}
                    onChange={debouncedApplyWidgetConfigChange}
                />

                <WidgetSettingsCheckboxValueEditor
                    propertyName={'showIndexPerf'}
                    config={config}
                    label={'Index Price'}
                    onChange={immediateApplyWidgetConfigChange}
                />

                <WidgetSettingsShowDataForEditor
                    config={config}
                    onChange={debouncedApplyWidgetConfigChange}
                />
            </div>

            <div className="group mappings-group">
                <div className="title">Comparison Index</div>
                <RadioGroup
                    selected={selectedIndexOption}
                    onChange={onChangeIndex}
                    options={selectedIndexOptions}
                    className={'comparison-index-radio'}
                />
                <div className="symbol-index-mappings">
                    &nbsp;({userPrefsDisplay})&nbsp;
                    {userPrefMappings && <IndexMappingsEditorModal />}
                </div>
            </div>

            <div className="group mappings-group">
                <div className="title">Capsule Title: Index Display</div>
                <RadioGroup
                    selected={selectedTitleOption}
                    onChange={onChangeTitle}
                    options={titleOptions}
                    className={'title-radio'}
                />
            </div>

            <WidgetNewportAppSettingsEditor />
        </div>
    );
}
